/**
 * Firebase Firestore collection and document path helpers
 * Centralized path management for consistency across the application
 */

// Root collections
export const COLLECTIONS = {
  GALLERIES: 'galleries',
  GALLERY_DOMAINS: 'galleryDomains',
  SUPERADMINS: 'superadmins',
  VIEWING_EVENTS: 'viewing_events',
  USER_PIECE_INTERACTIONS: 'user_piece_interactions',
  USER_PREFERENCES: 'user_preferences',
  FAVORITES: 'favorites',
  USER_STATS: 'userStats',
  PIECE_STATS: 'pieceStats',
  GLOBAL_SETTINGS: 'global_settings'
} as const;

// Gallery sub-collections
export const GALLERY_COLLECTIONS = {
  ARTISTS: 'artists',
  COLLECTIONS: 'collections',
  COLLECTION_PIECES: 'collectionPieces',
  PIECES: 'pieces',
  USERS: 'users',
  USER_SESSIONS: 'userSessions',
  PIECE_STATS: 'pieceStats',
  PIECE_VIEWS: 'pieceViews',
  ANALYTICS: 'analytics',
  ANALYTICS_CACHES: 'analyticsCaches',
  ANALYTICS_HISTORY: 'analyticsHistory',
  FAVORITES: 'favorites',
  FAVORITE_PIECES: 'favoritePieces',
  FRAME_FAMILIES: 'frameFamilies',
  FRAMES: 'frames',
  USER_FRAME_PREFERENCES: 'userFramePreferences',
  INTEGRITY_REPORTS: 'integrityReports'
} as const;

// Analytics sub-collections
export const ANALYTICS_COLLECTIONS = {
  DATA: 'data',
  CACHE: 'cache',
  HISTORY: 'history',
  SESSIONS: 'sessions',
  EVENTS: 'events',
  USER_PIECE_ROLLUPS: 'userPieceRollups',
  DAILY_PIECE_ROLLUPS: 'dailyPieceRollups',
  DAILY_ARTIST_ROLLUPS: 'dailyArtistRollups',
  DAILY_USER_ROLLUPS: 'dailyUserRollups'
} as const;

/**
 * Generate gallery document path
 */
export function getGalleryPath(galleryId: string): string {
  return `${COLLECTIONS.GALLERIES}/${galleryId}`;
}

/**
 * Generate public gallery domain routing document path
 */
export function getGalleryDomainPath(subdomain: string): string {
  return `${COLLECTIONS.GALLERY_DOMAINS}/${subdomain}`;
}

/**
 * Generate gallery sub-collection path
 */
export function getGalleryCollectionPath(galleryId: string, collection: string): string {
  return `${getGalleryPath(galleryId)}/${collection}`;
}

/**
 * Generate gallery document path within a sub-collection
 */
export function getGalleryDocumentPath(galleryId: string, collection: string, documentId: string): string {
  return `${getGalleryCollectionPath(galleryId, collection)}/${documentId}`;
}

/**
 * Generate artist collection path
 */
export function getArtistsPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.ARTISTS);
}

/**
 * Generate artist document path
 */
export function getArtistPath(galleryId: string, artistId: string): string {
  return getGalleryDocumentPath(galleryId, GALLERY_COLLECTIONS.ARTISTS, artistId);
}

/**
 * Generate collections collection path
 */
export function getCollectionsPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.COLLECTIONS);
}

/**
 * Generate collection document path
 */
export function getCollectionPath(galleryId: string, collectionId: string): string {
  return getGalleryDocumentPath(galleryId, GALLERY_COLLECTIONS.COLLECTIONS, collectionId);
}

/**
 * Generate collection membership pieces sub-collection path
 */
export function getCollectionPiecesPath(galleryId: string, collectionId: string): string {
  return `${getCollectionPath(galleryId, collectionId)}/${GALLERY_COLLECTIONS.COLLECTION_PIECES}`;
}

/**
 * Generate collection membership piece document path
 */
export function getCollectionPiecePath(galleryId: string, collectionId: string, pieceId: string): string {
  return `${getCollectionPiecesPath(galleryId, collectionId)}/${pieceId}`;
}

/**
 * Generate pieces collection path
 */
export function getPiecesPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.PIECES);
}

/**
 * Generate piece document path
 */
export function getPiecePath(galleryId: string, pieceId: string): string {
  return getGalleryDocumentPath(galleryId, GALLERY_COLLECTIONS.PIECES, pieceId);
}

/**
 * Generate users collection path
 */
export function getUsersPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.USERS);
}

/**
 * Generate user document path
 */
export function getUserPath(galleryId: string, userId: string): string {
  return getGalleryDocumentPath(galleryId, GALLERY_COLLECTIONS.USERS, userId);
}

/**
 * Generate user sessions collection path
 */
export function getUserSessionsPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.USER_SESSIONS);
}

/**
 * Generate user session document path
 */
export function getUserSessionPath(galleryId: string, sessionId: string): string {
  return getGalleryDocumentPath(galleryId, GALLERY_COLLECTIONS.USER_SESSIONS, sessionId);
}

/**
 * Generate analytics cache document path
 */
export function getAnalyticsCachePath(galleryId: string): string {
  return getLatestAnalyticsCachePath(galleryId);
}

/**
 * Generate analytics history collection path
 */
export function getAnalyticsHistoryPath(galleryId: string): string {
  return getGalleryAnalyticsHistoryPath(galleryId);
}

/**
 * Generate legacy nested analytics cache document path
 */
export function getLegacyAnalyticsCachePath(galleryId: string): string {
  return `${getGalleryPath(galleryId)}/${GALLERY_COLLECTIONS.ANALYTICS}/${ANALYTICS_COLLECTIONS.CACHE}`;
}

/**
 * Generate legacy nested analytics history collection path
 */
export function getLegacyAnalyticsHistoryPath(galleryId: string): string {
  return `${getGalleryPath(galleryId)}/${GALLERY_COLLECTIONS.ANALYTICS}/${ANALYTICS_COLLECTIONS.HISTORY}`;
}

/**
 * Generate analytics cache collection path used by deployed analytics functions
 */
export function getAnalyticsCachesPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.ANALYTICS_CACHES);
}

/**
 * Generate latest analytics cache document path used by deployed analytics functions
 */
export function getLatestAnalyticsCachePath(galleryId: string): string {
  return `${getAnalyticsCachesPath(galleryId)}/latest`;
}

/**
 * Generate analytics history collection path used by deployed analytics functions
 */
export function getGalleryAnalyticsHistoryPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.ANALYTICS_HISTORY);
}

/**
 * Generate canonical function-owned analytics root document path
 */
export function getCanonicalAnalyticsPath(galleryId: string): string {
  return `${getGalleryPath(galleryId)}/${GALLERY_COLLECTIONS.ANALYTICS}/${ANALYTICS_COLLECTIONS.DATA}`;
}

/**
 * Generate canonical analytics sessions collection path
 */
export function getCanonicalAnalyticsSessionsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.SESSIONS}`;
}

/**
 * Generate canonical analytics session document path
 */
export function getCanonicalAnalyticsSessionPath(galleryId: string, sessionId: string): string {
  return `${getCanonicalAnalyticsSessionsPath(galleryId)}/${sessionId}`;
}

/**
 * Generate canonical analytics events collection path
 */
export function getCanonicalAnalyticsEventsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.EVENTS}`;
}

/**
 * Generate canonical user-piece rollups collection path
 */
export function getCanonicalUserPieceRollupsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.USER_PIECE_ROLLUPS}`;
}

/**
 * Generate canonical user-piece rollup document path
 */
export function getCanonicalUserPieceRollupPath(galleryId: string, userId: string, pieceId: string): string {
  return `${getCanonicalUserPieceRollupsPath(galleryId)}/${userId}_${pieceId}`;
}

/**
 * Generate canonical daily piece rollups collection path
 */
export function getCanonicalDailyPieceRollupsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.DAILY_PIECE_ROLLUPS}`;
}

/**
 * Generate canonical daily artist rollups collection path
 */
export function getCanonicalDailyArtistRollupsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.DAILY_ARTIST_ROLLUPS}`;
}

/**
 * Generate canonical daily user rollups collection path
 */
export function getCanonicalDailyUserRollupsPath(galleryId: string): string {
  return `${getCanonicalAnalyticsPath(galleryId)}/${ANALYTICS_COLLECTIONS.DAILY_USER_ROLLUPS}`;
}

/**
 * Generate viewing event document path
 */
export function getViewingEventPath(eventId: string): string {
  return `${COLLECTIONS.VIEWING_EVENTS}/${eventId}`;
}

/**
 * Generate user piece interaction document path
 */
export function getUserPieceInteractionPath(galleryId: string, userId: string, pieceId: string): string {
  return `${COLLECTIONS.USER_PIECE_INTERACTIONS}/${galleryId}_${userId}_${pieceId}`;
}

/**
 * Generate user preferences document path
 */
export function getUserPreferencesPath(galleryId: string, userId: string): string {
  return `${COLLECTIONS.USER_PREFERENCES}/${galleryId}_${userId}`;
}

/**
 * Generate favorites collection path for a user
 */
export function getFavoritesPath(userId: string): string {
  return `${COLLECTIONS.FAVORITES}/${userId}`;
}

/**
 * Generate favorite piece document path
 */
export function getFavoritePiecePath(userId: string, pieceId: string): string {
  return `${getFavoritesPath(userId)}/${GALLERY_COLLECTIONS.FAVORITE_PIECES}/${pieceId}`;
}

/**
 * Generate gallery-scoped favorites collection path for a user
 */
export function getGalleryFavoritesPath(galleryId: string, userId: string): string {
  return `${getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.FAVORITES)}/${userId}`;
}

/**
 * Generate gallery-scoped favorite piece document path
 */
export function getGalleryFavoritePiecePath(galleryId: string, userId: string, pieceId: string): string {
  return `${getGalleryFavoritesPath(galleryId, userId)}/${GALLERY_COLLECTIONS.FAVORITE_PIECES}/${pieceId}`;
}

/**
 * Generate gallery-scoped favorite pieces collection path for a user
 */
export function getGalleryFavoritePiecesPath(galleryId: string, userId: string): string {
  return `${getGalleryFavoritesPath(galleryId, userId)}/${GALLERY_COLLECTIONS.FAVORITE_PIECES}`;
}

/**
 * Generate gallery integrity reports collection path
 */
export function getIntegrityReportsPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.INTEGRITY_REPORTS);
}

/**
 * Generate gallery integrity report document path
 */
export function getIntegrityReportPath(galleryId: string, reportId: string): string {
  return `${getIntegrityReportsPath(galleryId)}/${reportId}`;
}

/**
 * Generate legacy root piece stats document path
 */
export function getPieceStatsPath(pieceId: string): string {
  return `${COLLECTIONS.PIECE_STATS}/${pieceId}`;
}

/**
 * Generate gallery-scoped piece stats collection path
 */
export function getGalleryPieceStatsPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.PIECE_STATS);
}

/**
 * Generate gallery-scoped piece stats document path
 */
export function getGalleryPieceStatsDocumentPath(galleryId: string, pieceId: string): string {
  return `${getGalleryPieceStatsPath(galleryId)}/${pieceId}`;
}

/**
 * Generate frame families collection path
 */
export function getFrameFamiliesPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.FRAME_FAMILIES);
}

/**
 * Generate frame family document path
 */
export function getFrameFamilyPath(galleryId: string, familyId: string): string {
  return `${getFrameFamiliesPath(galleryId)}/${familyId}`;
}

/**
 * Generate frames collection path within a frame family
 */
export function getFramesPath(galleryId: string, familyId: string): string {
  return `${getFrameFamilyPath(galleryId, familyId)}/${GALLERY_COLLECTIONS.FRAMES}`;
}

/**
 * Generate frame document path
 */
export function getFramePath(galleryId: string, familyId: string, frameId: string): string {
  return `${getFramesPath(galleryId, familyId)}/${frameId}`;
}

/**
 * Generate user frame preferences collection path
 */
export function getUserFramePreferencesPath(galleryId: string): string {
  return getGalleryCollectionPath(galleryId, GALLERY_COLLECTIONS.USER_FRAME_PREFERENCES);
}

/**
 * Generate user frame preferences document path
 */
export function getUserFramePreferencesDocumentPath(galleryId: string, userId: string): string {
  return `${getUserFramePreferencesPath(galleryId)}/${userId}`;
}

/**
 * Generate user stats document path
 */
export function getUserStatsPath(userId: string): string {
  return `${COLLECTIONS.USER_STATS}/${userId}`;
}
